import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { linkifyWithBreaks } from '@/utils/linkify';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Heart, MessageCircle, Share2, X, Send } from 'lucide-react';
import { pb, InspirePost, InspireComment } from '@/lib/pocketbase';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { detectTextDirection } from '@/utils/textDirection';
import { CommentItem } from '@/components/inspire/CommentItem';

interface PostDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: InspirePost;
  isLiked: boolean;
  onLike: () => void;
  onShare: () => void;
  userId?: string;
  onAuthRequired?: () => void;
}

export const PostDetailModal = ({ 
  isOpen, 
  onClose, 
  post,
  isLiked: initialIsLiked,
  onLike,
  onShare,
  userId,
  onAuthRequired
}: PostDetailModalProps) => {
  const navigate = useNavigate();
  const [comments, setComments] = useState<InspireComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(true);
  
  // Local state for like status and count (synced from parent)
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);

  // Sync with props when they change (parent handles real-time updates)
  useEffect(() => {
    setIsLiked(initialIsLiked);
    setLikesCount(post.likes_count || 0);
  }, [initialIsLiked, post.likes_count, post.id]);

  const getTutorInfo = () => {
    const tutor = post.expand?.tutor_id;
    const tutorUser = tutor?.expand?.user;
    
    let avatar = '';
    if (tutor?.profilePictureUrl) {
      avatar = tutor.profilePictureUrl;
    } else if (tutor?.profilePicture) {
      avatar = pb.files.getURL(tutor, tutor.profilePicture);
    } else if (tutorUser?.avatar) {
      avatar = tutorUser.avatar;
    }
    
    return {
      name: tutor?.name || tutorUser?.name || 'Unknown Tutor',
      avatar,
      tutorId: tutor?.id || ''
    };
  };

  const tutorInfo = getTutorInfo();

  const getImageUrl = () => {
    if (!post.image) return null;
    return `${pb.baseUrl}/api/files/inspire_posts/${post.id}/${post.image}`;
  };

  const imageUrl = getImageUrl();

  // Fetch comments with tutor information - only top-level comments (no parent)
  const fetchComments = async () => {
    setIsLoadingComments(true);
    try {
      const records = await pb.collection('inspire_comments').getFullList({
        filter: `post_id="${post.id}" && parent_comment_id=""`,
        sort: '-is_pinned,-created', // Pinned comments first, then by creation date
        expand: 'user_id'
      });
      
      // For each comment, check if the user is a tutor and get their tutor profile
      const commentsWithTutorInfo = await Promise.all(
        records.map(async (comment) => {
          try {
            // Query tutors collection where user field matches the comment's user_id
            const tutorRecords = await pb.collection('tutors').getFullList({
              filter: `user="${comment.user_id}"`,
              limit: 1
            });
            
            if (tutorRecords.length > 0) {
              return { ...comment, tutorRecord: tutorRecords[0] };
            }
            return comment;
          } catch (error) {
            return comment;
          }
        })
      );
      
      setComments(commentsWithTutorInfo as InspireComment[]);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setIsLoadingComments(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchComments();
    }
  }, [isOpen, post.id]);

  // Real-time comment subscription
  useEffect(() => {
    if (!isOpen) return;

    const unsubscribe = pb.collection('inspire_comments').subscribe('*', (e) => {
      if (e.action === 'create' && e.record.post_id === post.id) {
        fetchComments();
      }
    });

    return () => {
      unsubscribe.then(unsub => unsub()).catch(console.error);
    };
  }, [isOpen, post.id]);

  const handleAddComment = async () => {
    if (!userId) {
      toast.error('Please sign in to comment');
      return;
    }

    const content = newComment.trim();
    if (!content) return;

    setIsSubmitting(true);
    try {
      console.log('Creating comment for post:', post.id, 'user:', userId);
      
      const newCommentRecord = await pb.collection('inspire_comments').create({
        post_id: post.id,
        user_id: userId,
        content
      });

      setNewComment('');
      toast.success('Comment added successfully');
      fetchComments();
      
      // Send notification to post author
      try {
        console.log('🔔 Checking notification conditions:', {
          postAuthor: post.expand?.tutor_id?.user,
          currentUser: userId,
          shouldNotify: post.expand?.tutor_id?.user && post.expand.tutor_id.user !== userId
        });
        
        if (post.expand?.tutor_id?.user && post.expand.tutor_id.user !== userId) {
          console.log('🔔 Creating notification for post author:', post.expand.tutor_id.user);
          const currentUser = await pb.collection('users').getOne(userId);
          
          await pb.collection('notifications').create({
            userId: post.expand.tutor_id.user,
            type: 'post_comment',
            title: 'New comment on your post',
            message: `${currentUser.name || 'Someone'} commented on your post`,
            isRead: false,
            createdAt: new Date().toISOString(),
            data: {
              postId: post.id
            }
          });
        }
      } catch (notifError: any) {
        console.error('❌ Failed to create notification:', notifError);
        console.error('Error details:', {
          message: notifError.message,
          status: notifError.status,
          data: notifError.data,
          response: notifError.response
        });
        const errorMsg = notifError.data?.message || notifError.message || 'Unknown error';
        toast.error('❌ Notification error: ' + errorMsg);
      }
      
      // The comment count will be updated via real-time subscription
    } catch (error: any) {
      console.error('Error adding comment:', error);
      
      // Show detailed error message
      let errorMessage = 'Failed to add comment. ';
      if (error?.response?.message) {
        errorMessage += error.response.message;
      } else if (error?.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please check your connection and try again.';
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTutorClick = () => {
    if (tutorInfo.tutorId) {
      onClose();
      navigate(`/tutor/${tutorInfo.tutorId}`);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0 w-[95vw] sm:w-full">
        <DialogHeader className="px-6 pt-6 pb-0">
          <div className="flex items-center justify-between">
            <DialogTitle>Post Details</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-80px)]">
          <div className="px-6 pb-20 space-y-6">
            {/* Tutor Info */}
            <div className="flex items-center gap-3">
              <Avatar 
                className="cursor-pointer hover:opacity-80 transition-opacity h-12 w-12"
                onClick={handleTutorClick}
              >
                <AvatarImage src={tutorInfo.avatar} />
                <AvatarFallback>{tutorInfo.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <p 
                  className="font-semibold cursor-pointer hover:text-primary transition-colors"
                  onClick={handleTutorClick}
                >
                  {tutorInfo.name}
                </p>
                <p className="text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(post.created), { addSuffix: true })}
                </p>
              </div>
            </div>

            {/* Post Content */}
            <div className="space-y-4">
              <div 
                className="whitespace-pre-wrap break-words text-base leading-relaxed"
                dir={detectTextDirection(post.content)}
                dangerouslySetInnerHTML={{ __html: linkifyWithBreaks(post.content) }}
              />
              
              {imageUrl && (
                <img 
                  src={imageUrl} 
                  alt="Post image" 
                  className="w-full rounded-lg object-contain max-h-[500px] md:max-h-none md:object-cover"
                />
              )}
            </div>

            {/* Action Stats */}
            <div className="flex items-center gap-6 py-3 border-t border-b">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (!userId && onAuthRequired) {
                    onAuthRequired();
                  } else {
                    // Only update heart color, let real-time subscription handle count
                    setIsLiked(!isLiked);
                    onLike();
                  }
                }}
                className="flex items-center gap-2 touch-manipulation active:scale-95 transition-transform"
              >
                <Heart className={`h-5 w-5 transition-all ${isLiked ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />
                <span className={isLiked ? 'text-red-500' : 'text-muted-foreground'}>{likesCount}</span>
              </Button>
              
              <div className="flex items-center gap-2 text-muted-foreground">
                <MessageCircle className="h-5 w-5" />
                <span>{post.comments_count}</span>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={onShare}
                className="flex items-center gap-2"
              >
                <Share2 className="h-5 w-5" />
                <span>{post.shares_count}</span>
              </Button>
            </div>

            {/* Comments Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Comments</h3>
              
              {/* Add Comment - Only show input if logged in */}
              {userId ? (
                <div className="flex flex-col sm:flex-row gap-3 pb-4 border-b">
                  <Textarea
                    placeholder="Write a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="min-h-[80px] resize-none"
                    dir="auto"
                    style={{ unicodeBidi: 'embed' }}
                  />
                  <Button
                    size="sm"
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || isSubmitting}
                    className="self-end w-full sm:w-auto"
                  >
                    <Send className="h-4 w-4 mr-2 sm:mr-0" />
                    <span className="sm:hidden">Post Comment</span>
                  </Button>
                </div>
              ) : (
                <div className="pb-4 border-b">
                  <Button
                    onClick={onAuthRequired}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    Sign in to comment
                  </Button>
                </div>
              )}

              {/* Comments List */}
              <div className="space-y-4">
                {isLoadingComments ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Loading comments...
                  </div>
                ) : comments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No comments yet. Be the first to comment!
                  </div>
                ) : (
                  comments.map(comment => (
                    <CommentItem
                      key={comment.id}
                      comment={comment}
                      userId={userId}
                      postOwnerId={post.expand?.tutor_id?.user}
                      onDelete={fetchComments}
                      onEdit={fetchComments}
                      onAuthRequired={onAuthRequired}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
